/**
 * Saving the founder's edits into the builder's copy of the prototype (`PUT …/versions/:id/files`),
 * so the next edit turn reads, patches and builds from them.
 *
 * Before, the save ran once and only when the version id was already known: an edit saved right
 * after a build, before the version was recorded, was skipped, and a failed request was only
 * logged. The next change then rewrote the file from the builder's stale copy and the edit was
 * lost with nothing on screen saying so (production test of 19 Sep).
 *
 * - Waits up to `waitForVersionMs` for the version id.
 * - Retries a failed request once after `retryDelayMs`.
 * - A 409 means the version is not the prototype's newest (or a build is running): nothing to
 *   save there, so it is `skipped`, not `failed`.
 */
/** The workbench's Retry on a failed save; the facade listens for it. */
export const WORKBENCH_BUILDER_SAVE_RETRY_EVENT = 'ideoz-workbench-builder-save-retry';

export type BuilderSaveOutcome = 'saved' | 'skipped' | 'failed';

export const BUILDER_SAVE_WAIT_FOR_VERSION_MS = 10_000;
export const BUILDER_SAVE_POLL_MS = 500;
export const BUILDER_SAVE_RETRY_DELAY_MS = 2_000;

export async function saveEditsToBuilder(args: {
  /** The version id of the turn being saved, once known. */
  getVersionId: () => string | undefined;
  save: (versionId: string) => Promise<unknown>;
  sleep?: (ms: number) => Promise<void>;
  waitForVersionMs?: number;
  pollMs?: number;
  retryDelayMs?: number;
}): Promise<BuilderSaveOutcome> {
  const sleep = args.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const waitMs = args.waitForVersionMs ?? BUILDER_SAVE_WAIT_FOR_VERSION_MS;
  const pollMs = args.pollMs ?? BUILDER_SAVE_POLL_MS;

  let versionId = args.getVersionId();
  for (let waited = 0; !versionId && waited < waitMs; waited += pollMs) {
    await sleep(pollMs);
    versionId = args.getVersionId();
  }
  if (!versionId) return 'failed';

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      await args.save(versionId);
      return 'saved';
    } catch (err) {
      if (httpStatus(err) === 409) return 'skipped';
      if (attempt === 0) await sleep(args.retryDelayMs ?? BUILDER_SAVE_RETRY_DELAY_MS);
    }
  }
  return 'failed';
}

function httpStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { status?: unknown; response?: { status?: unknown } };
  const status = e.response?.status ?? e.status;
  return typeof status === 'number' ? status : undefined;
}
